import React, { useRef } from 'react';
import clsx from 'clsx';
import { Smile } from 'lucide-react';
import { InspectorHeader } from '../InspectorHeader/InspectorHeader';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useNode } from '@craftjs/core';
import type { IconProps } from '@builder4/shared/components/Icon';
import type { IconStyle } from '@builder4/shared/types/icon';
import { LayoutElementV2 } from '@/components/inspector-fields/LayoutElement/LayoutElementV2';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { SliderField } from '@/components/inspector-fields/SliderField/SliderField';
import { IconField } from '@/components/inspector-fields/IconField/IconField';
import { ICON_STYLE_REGULAR } from '@builder4/shared/utils/constants';
import type { Color, RGBA } from '@builder4/shared/types/layout';
import {
  isColorReference,
  resolveColorReference,
  rgbaToHexa,
} from '@builder4/shared/utils/styleHelpers';
import { ColorField } from '@/components/inspector-fields/ColorField/ColorField';
import { useAppSelector } from '@/store/hooks';
import { selectActiveGlobalStyle } from '@/store/globalStyles/selectors';
import { createPaletteFromGlobalColors } from '@/utils/colorPaletteHelpers';
import NodeColorField from '@/components/inspector-fields/ColorField/NodeColorField';
import NodeStrokeSettings from '@/components/inspector-fields/SliderField/NodeStrokeSettings';
import NodeShadowField from '@/components/inspector-fields/ShadowField/NodeShadowField';

interface InspectorIconProps {
  className?: string;
  dark?: boolean;
  text?: string;
}

export const InspectorIcon: React.FC<InspectorIconProps> = ({
  className,
  dark = false,
  text = 'Icon',
}) => {
  const activeStyles = useAppSelector(selectActiveGlobalStyle);

  const {
    actions: { setProp },
    props,
    displayName,
  } = useNode((node) => ({
    props: node.data.props as IconProps,
    displayName: node.data.displayName,
  }));

  const { getBPProp, setBPProp } = useBreakpointAwareProp(displayName);

  const iconName = getBPProp(props, 'iconName') || '';
  const iconStyle = getBPProp(props, 'iconStyle') || ICON_STYLE_REGULAR;
  const colorProp = getBPProp(props, 'color');
  const resolvedColor = isColorReference(colorProp)
    ? resolveColorReference(colorProp, activeStyles.colors)
    : colorProp;
  const color = (resolvedColor || { r: 0, g: 0, b: 0, a: 1 }) as RGBA;
  const opacity = (getBPProp(props, 'opacity') ?? 1) * 100;

  const handleIconSelect = (name: string, style: IconStyle) => {
    setProp((props: IconProps) => {
      setBPProp(props, 'iconName', name);
      setBPProp(props, 'iconStyle', style);
    }, 500);
  };

  const handleColorChange = (value: RGBA | undefined) => {
    if (!value) return;
    setProp((props: IconProps) => {
      setBPProp(props, 'color', value);
    });
  };

  // WHY: Two setProp calls create proper undo/redo history entry in CraftJS
  const handleColorChangeComplete = (newColor: Color | string, initialColor: Color | string) => {
    setProp((props: IconProps) => {
      setBPProp(props, 'color', initialColor as IconProps['color']);
    });
    setProp((props: IconProps) => {
      setBPProp(props, 'color', newColor as IconProps['color']);
    }, 500);
  };

  // WHY: UI displays 0-100 percentage but prop stores 0-1 decimal
  const handleOpacityChange = (value: number) => {
    setProp((props: IconProps) => {
      setBPProp(props, 'opacity', value / 100);
    });
  };

  const handleOpacityChangeComplete = (value: number, initialValue: number) => {
    setProp((props: IconProps) => {
      setBPProp(props, 'opacity', initialValue / 100);
    });
    setProp((props: IconProps) => {
      setBPProp(props, 'opacity', value / 100);
    }, 500);
  };

  const colorHex = rgbaToHexa(color);
  // WHY: Ref gives IconField a portal target outside ScrollArea so the picker isn't clipped
  const inspectorRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={inspectorRef}
      className={clsx(
        'inspector bg-neutral-0 relative z-10 inline-flex h-[calc(100vh-52px)] max-w-[240px] flex-col overflow-visible border-l border-neutral-100',
        className,
        { dark },
      )}
      data-testid="inspector-icon"
    >
      <InspectorHeader className="max-w-[240px] text-neutral-900" icon={Smile} text={text} />
      <ScrollArea className="h-[calc(100vh-104px)] overflow-hidden">
        <div className="flex flex-col gap-3 p-3">
          <LayoutElementV2
            className=""
            state="default"
            data-qa-selector="inspector-icon-layout-element"
          />

          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

          <p className="pb-1 text-xs font-semibold text-neutral-900">Icon</p>

          <IconField
            className="max-w-[216px]"
            iconName={iconName}
            iconStyle={iconStyle}
            onSelect={handleIconSelect}
            pickerContainerRef={inspectorRef}
            data-qa-selector="inspector-icon-picker"
          />

          <ColorField
            palettes={[createPaletteFromGlobalColors(activeStyles.colors)]}
            className="max-w-[216px]"
            mode="solid"
            inputValue={colorHex}
            placeholderText="-"
            state="default"
            text="Color"
            type="text"
            color={color}
            onChange={(value) => handleColorChange(value as RGBA)}
            onChangeComplete={handleColorChangeComplete}
            hasGradient={false}
            data-qa-selector="inspector-icon-color"
          />

          <SliderField
            className="max-w-[216px]"
            label="Opacity"
            value={opacity}
            min={0}
            max={100}
            step={1}
            onChange={handleOpacityChange}
            onChangeComplete={handleOpacityChangeComplete}
            data-qa-selector="inspector-icon-opacity"
          />

          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

          <p className="pb-1 text-xs font-semibold text-neutral-900">Appearance</p>

          <NodeColorField
            propertyName="backgroundColor"
            solidPropertyName="bgSolid"
            gradientPropertyName="bgGradient"
            gradientEnabled
            label="Fill"
            data-qa-selector="inspector-icon-fill"
          />
          <NodeColorField
            propertyName="borderColor"
            ensureStrokeVisibleProp="borderWidth"
            label="Stroke Color"
            data-qa-selector="inspector-icon-stroke-color"
          />
          <NodeStrokeSettings max={10} data-qa-selector="inspector-icon-stroke" />
          <NodeShadowField
            shadowTypeProp="fieldShadowType"
            shadowSettingsProp="fieldShadowSettings"
            data-qa-selector="inspector-icon-shadow"
          />
        </div>
      </ScrollArea>
    </div>
  );
};
